import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const API = process.env.REACT_APP_BACKEND_URL;

const ApiSettings = () => {
  const [status, setStatus] = useState(null);
  const [accountId, setAccountId] = useState('');
  const [token, setToken] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [testResult, setTestResult] = useState(null); // {ok, message}

  const authHeaders = () => ({ 'Authorization': `Bearer ${localStorage.getItem('adminToken')}`, 'Content-Type': 'application/json' });

  useEffect(() => { fetchStatus(); }, []);

  const fetchStatus = async () => {
    try {
      const res = await fetch(`${API}/api/admin/instagram/settings`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
        setAccountId(data.business_account_id || '');
      }
    } catch (e) { /* noop */ }
  };

  const save = async () => {
    setSaving(true); setSaveMsg(''); setTestResult(null);
    try {
      const body = { business_account_id: accountId };
      if (token.trim()) body.access_token = token.trim();
      const res = await fetch(`${API}/api/admin/instagram/settings`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(body) });
      const data = await res.json();
      if (res.ok) {
        setStatus(data);
        setToken('');
        setSaveMsg('Settings saved securely.');
      } else {
        setSaveMsg(data.detail || 'Failed to save settings');
      }
    } catch (e) { setSaveMsg('Connection error'); }
    finally { setSaving(false); }
  };

  const testConnection = async () => {
    setTesting(true); setTestResult(null); setSaveMsg('');
    try {
      const res = await fetch(`${API}/api/admin/instagram/test-connection`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (res.ok && data.success) {
        setTestResult({ ok: true, message: `Connected to @${data.profile.username} (${data.profile.name})` });
      } else {
        const detail = data.detail;
        const msg = typeof detail === 'string' ? detail : (detail?.message || 'Connection failed');
        setTestResult({ ok: false, message: msg });
      }
    } catch (e) { setTestResult({ ok: false, message: 'Connection error' }); }
    finally { setTesting(false); }
  };

  return (
    <div className="max-w-2xl" data-testid="api-settings">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">API Settings</h1>
        <p className="text-gray-500 text-sm mt-1">Connect your Instagram Business account for direct publishing. Credentials are stored securely on the server and never shown in full.</p>
      </div>

      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-5">
          <span className="text-lg">📸</span>
          <h2 className="font-semibold text-gray-900">Instagram (Graph API)</h2>
          {status?.configured && (
            <span className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700" data-testid="ig-configured-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Configured
            </span>
          )}
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Instagram Business Account ID</label>
            <input type="text" value={accountId} onChange={(e) => setAccountId(e.target.value)} data-testid="ig-account-id"
              placeholder="17841400000000000"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Access Token (System User)</label>
            <input type="password" value={token} onChange={(e) => setToken(e.target.value)} data-testid="ig-access-token"
              placeholder={status?.has_token ? `Saved (${status.token_preview}) — leave blank to keep` : 'Paste your never-expiring token'}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
            <p className="text-xs text-gray-400 mt-1">Stored as a server environment variable. Never sent back to the browser.</p>
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-2">
            <button onClick={save} disabled={saving} data-testid="save-ig-settings-btn"
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-lg text-sm font-medium">
              {saving ? 'Saving…' : 'Save Settings'}
            </button>
            <button onClick={testConnection} disabled={testing || !status?.configured} data-testid="test-connection-btn"
              className="px-4 py-2 border border-gray-300 hover:bg-gray-50 disabled:opacity-50 text-gray-700 rounded-lg text-sm font-medium inline-flex items-center gap-2">
              {testing ? (<><span className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500" /> Testing…</>) : 'Test Connection'}
            </button>
            {saveMsg && <span className="text-sm text-gray-600" data-testid="save-msg">{saveMsg}</span>}
          </div>

          {testResult && (
            <div data-testid="test-result" className={`mt-2 p-3 rounded-lg text-sm ${testResult.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              {testResult.ok ? '✓ ' : '✕ '}{testResult.message}
            </div>
          )}
        </div>
      </motion.div>

      <div className="mt-4 text-xs text-gray-400">
        TikTok auto-posting is not enabled yet. Instagram only for this phase.
      </div>
    </div>
  );
};

export default ApiSettings;
